import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { BrandLogo } from "@/components/common/BrandIdentity";
import { Button } from "@/components/ui/button";
import { CreateOrgWizard } from "@/components/shell/CreateOrgWizard";
import { Building2, ArrowRight, Plus, Sparkles } from "lucide-react";

// One-time organisation chooser. Shown before the workspace loads when a user has multiple ACTIVE
// memberships and no resolvable default, OR (onboarding) when a brand-new user has no organisation yet.
export default function OrgPicker({ onboarding = false }) {
  const { ccore, switchOrg } = useApp();
  const [busy, setBusy] = useState(null);
  const [wizard, setWizard] = useState(false);
  const active = (ccore?.memberships || []).filter((m) => m.status === "ACTIVE");

  const choose = async (mid) => {
    setBusy(mid);
    try { await switchOrg(mid); } catch (e) { setBusy(null); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6" data-testid="org-picker">
      <div className="w-full max-w-lg">
        <div className="mb-8 flex items-center gap-3">
          <BrandLogo className="h-12 w-12" testId="org-picker-brand" />
          <div>
            <div className="font-display text-2xl font-semibold text-ink">{onboarding ? "Welcome to Nyayyaksha" : "Choose organisation"}</div>
            <p className="text-sm text-ink-2">{onboarding ? "Set up your practice to get started — it takes less than a minute." : "You belong to multiple organisations. Select one to continue."}</p>
          </div>
        </div>

        {onboarding ? (
          <button onClick={() => setWizard(true)} data-testid="onboarding-create-org"
            className="group flex w-full items-center justify-between rounded-xl border border-copper/40 bg-copper-light/40 p-5 text-left transition-colors hover:border-copper">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-copper/15"><Sparkles className="h-5 w-5 text-copper" /></div>
              <div><div className="font-medium text-ink">Create your organisation</div><div className="text-[12px] text-ink-muted">Firm, chamber, sole practice, or a government / in-house team</div></div>
            </div>
            <ArrowRight className="h-4 w-4 text-copper transition-transform group-hover:translate-x-1" />
          </button>
        ) : (
          <>
            <div className="space-y-3">
              {active.map((m) => (
                <button key={m.membershipId} data-testid={`org-picker-item-${m.membershipId}`} disabled={!!busy} onClick={() => choose(m.membershipId)}
                  className="group flex w-full items-center justify-between rounded-xl border border-line bg-card p-4 text-left transition-colors hover:border-copper/50 disabled:opacity-60">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-copper/10"><Building2 className="h-5 w-5 text-copper" /></div>
                    <div>
                      <div className="font-medium text-ink">{m.orgName}</div>
                      <div className="text-[11px] uppercase tracking-wide text-ink-muted">{(m.roles || []).join(", ") || "member"}</div>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-ink-muted transition-transform group-hover:translate-x-1" />
                </button>
              ))}
            </div>
            <Button variant="ghost" className="mt-4 w-full border border-dashed border-line text-ink-2 hover:text-copper" onClick={() => setWizard(true)} data-testid="org-picker-create-org">
              <Plus className="mr-1 h-4 w-4" /> Create a new organisation
            </Button>
          </>
        )}
      </div>
      <CreateOrgWizard open={wizard} onClose={() => setWizard(false)} />
    </div>
  );
}
